import IconWarningFill from 'assets/Icons/Check/IconWarningFill';
import { SubTitle, SubTitleLayout, SubTitleText, SubTitleTextContainer } from 'components/Hospital/ViewDetailDiagnosisPageSubTitleForms';
import React, { ChangeEvent, Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { BackgroundColor, BorderColor, TextColor, WarningColor } from 'styles/Global/colorSemantic';
import { Radius } from 'styles/Global/objectStyles';
import { Body1SemiBold, Body3Regular, Body3SemiBold, Body4Medium, Body4Regular } from 'styles/Global/typography';
import { OutlineBadge, SelectFilterBadge } from 'components/Common/Badge';
import { CLMessageBedge } from 'pages/Admin/CL/constants';
import { CLMessageTextArea, DCKTextArea } from 'styles/Global/TextAreaStyles';
import { CLLogo } from 'assets';
import BasicButton from 'components/Buttons/BasicButton';
import { formatExampleMessage } from 'pages/Admin/CL/utils';

interface CLMessageDeliveryProps {
    messageType: string;
    handleMessageType: (type: string) => void;
    message: string;
    setMessage: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    onSendMessageModal: () => void;
    hospitalName: string;
}

const CLMessageDelivery = ({ messageType, handleMessageType, message, setMessage, onSendMessageModal, hospitalName }: CLMessageDeliveryProps) => {
    return (
        <SubTitleLayout>
            <SubTitle>메시지 전송</SubTitle>
            <SubTitleTextContainer>
                <SubTitleText>칼로리랩을 통해서 환자에게 앱 알림 메시지를 전송할 수 있습니다.</SubTitleText>
                <SubTitleText>해당 환자에게만 전하고 싶은 메시지가 있다면 작성해보세요!</SubTitleText>
            </SubTitleTextContainer>
            <MessageDeliveryLayout>
                <MessageDeliveryBox>
                    <Body1SemiBold>메시지 작성</Body1SemiBold>
                    <MessageDesc>
                        <IconWarningFill width='16px' height='16px' />
                        개인정보와 관련된 민감한 정보는 작성하지 마세요. (이름, 생년월일, 연락처 등)
                    </MessageDesc>
                    <MessageBox>
                        <BedgeContainer>
                            {CLMessageBedge.map((item, idx) => (
                                <OutlineBadge key={item.value} $selected={messageType === item.value} onClick={() => handleMessageType(item.value)}>
                                    {item.label}
                                </OutlineBadge>
                            ))}
                        </BedgeContainer>
                        <CLMessageTextArea
                            // style={{ marginTop: '36px' }}
                            minRows={4}
                            maxRows={4}
                            maxLength={100}
                            placeholder='환자에게 전송할 메시지를 작성해주세요.'
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e)}
                            value={message}
                        />
                        <MessageLengthWrapper>
                            <MessageLength>{message.length}</MessageLength>/100
                        </MessageLengthWrapper>
                    </MessageBox>
                </MessageDeliveryBox>
                <MessageDeliveryBox>
                    <ColumnContainer>
                        <Body1SemiBold>전송 예시</Body1SemiBold>
                        <MessageDesc $color={TextColor.color_text_light}>아래와 같이 전송될 예정입니다. 확인 후에 환자에게 전송해보세요!</MessageDesc>
                        <MessageExampleBox>
                            <IconWrapper>
                                <Icon src={CLLogo} alt='CLLogo' />
                            </IconWrapper>
                            <MessageExampleContainer>
                                {/* TODO: 해당 병원에 따른 병원명 수정 */}
                                <Body3SemiBold>{formatExampleMessage(messageType, hospitalName)}</Body3SemiBold>
                                <MessageExampleContent $color={!message ? TextColor.color_text_weak : TextColor.color_text_normal}>
                                    {!message ? '작성된 메시지가 없습니다.' : message}
                                </MessageExampleContent>
                            </MessageExampleContainer>
                        </MessageExampleBox>
                    </ColumnContainer>
                    <ButtonContainer>
                        <BasicButton $width='260px' $height='60px' $type='fill' disabled={!message} onClick={onSendMessageModal}>
                            이대로 전송하기
                        </BasicButton>
                    </ButtonContainer>
                </MessageDeliveryBox>
            </MessageDeliveryLayout>
        </SubTitleLayout>
    );
};

export default CLMessageDelivery;

const MessageDeliveryLayout = styled.div`
    display: flex;
    gap: 36px;
    margin-top: 28px;
`;

const MessageDeliveryBox = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    border: 1px solid ${BorderColor.color_border_light};
    border-radius: ${Radius.radius_8};
    padding: 28px 32px;
`;

const MessageDesc = styled(Body4Medium)<{ $color?: string }>`
    display: flex;
    gap: 6px;
    align-items: center;
    width: 100%;
    height: 48px;
    color: ${(props) => props.$color || WarningColor.color_warning_normal};
    padding: 10px 0px 16px 0px;
`;

const MessageBox = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px 24px;
    border: 1px solid ${BorderColor.color_border_light};
    border-radius: ${Radius.radius_8};
`;

const BedgeContainer = styled.div`
    display: flex;
    gap: 6px;
    padding-bottom: 18px;
    border-bottom: 1px solid ${BorderColor.color_border_weak};
    margin-bottom: 16px;
`;

const MessageLengthWrapper = styled(Body4Regular)`
    display: flex;
    justify-content: flex-end;
    color: ${TextColor.color_text_weak};
`;

const MessageLength = styled(Body4Regular)`
    color: ${TextColor.color_text_neutral};
`;

const MessageExampleBox = styled.div`
    display: flex;
    gap: 16px;
    padding: 20px 28px 24px 20px;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: ${Radius.radius_12};
    background-color: ${BackgroundColor.color_background_light};
`;

const IconWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 54px;
`;

const Icon = styled.img`
    width: 22px;
    height: 22px;
`;

const MessageExampleContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: calc(100% - 48px);
    word-wrap: break-word;
    word-break: break-all;
`;

const MessageExampleContent = styled(Body4Regular)<{ $color?: string }>`
    color: ${(props) => props.$color || TextColor.color_text_normal};
`;

const ColumnContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ButtonContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
`;
